import React, { useContext, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  Paper,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import DirectionsCarOutlinedIcon from "@material-ui/icons/DirectionsCarOutlined";
import BusinessOutlinedIcon from "@material-ui/icons/BusinessOutlined";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import moment from "moment";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { OAuth as OAuthActions } from "../../js/actions";
import { Context } from "../../js/utils/Context";
import { setSnackbar } from "../../js/reducers/snackbar";
import { setAlertDialog } from "../../js/reducers/alertDialog";

const ACCENT = "#1d4ed8";
const ACCENT_DARK = "#1e40af";
const BORDER = "#e5e7eb";
const TEXT_TITLE = "#0f172a";
const TEXT_LABEL = "#94a3b8";
const TEXT_MUTED = "#64748b";
const SURFACE = "#ffffff";
const FIELD_BG = "#f1f5f9";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    padding: theme.spacing(1.5, 1.5),
    backgroundColor: SURFACE,
    fontFamily: theme.typography.fontFamily,
  },
  headerRow: {
    paddingBottom: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${BORDER}`,
  },
  headerTitle: {
    fontWeight: 700,
    color: TEXT_TITLE,
    fontSize: "1.1rem !important",
    lineHeight: 1.2,
    letterSpacing: "-0.01em",
  },
  headerSubtitle: {
    color: TEXT_MUTED,
    fontSize: "0.78rem !important",
    fontWeight: 400,
    marginTop: 2,
  },
  summaryCard: {
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: SURFACE,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  summaryHeader: {
    padding: theme.spacing(1.25, 1.75),
    background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
  },
  summaryHeaderEmpty: {
    background: "#f3f4f6",
    color: TEXT_MUTED,
  },
  iconBubble: {
    width: 40,
    height: 40,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.2)",
    color: "white",
    flexShrink: 0,
  },
  iconBubbleEmpty: {
    backgroundColor: SURFACE,
    color: TEXT_MUTED,
    border: `1px solid ${BORDER}`,
  },
  icon: {
    fontSize: "22px !important",
  },
  summaryTitleBox: {
    minWidth: 0,
    flex: 1,
  },
  summaryKicker: {
    fontSize: "0.65rem !important",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    opacity: 0.85,
    lineHeight: 1,
  },
  summaryTitle: {
    fontSize: "0.95rem !important",
    fontWeight: 700,
    lineHeight: 1.25,
    marginTop: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  summaryBody: {
    padding: theme.spacing(1.5, 1.75),
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "flex-start",
    gap: theme.spacing(3),
    flex: 1,
    overflowX: "auto",
  },
  summaryBodyItem: {
    minWidth: 0,
    flex: "1 1 0",
  },
  summaryBodyEmpty: {
    padding: theme.spacing(2.5, 1.75),
    color: TEXT_MUTED,
    fontSize: "0.85rem !important",
  },
  summaryFieldLabel: {
    color: TEXT_LABEL,
    fontSize: "0.66rem !important",
    fontWeight: 500,
    textTransform: "uppercase",
    marginBottom: 2,
  },
  summaryFieldValue: {
    color: TEXT_TITLE,
    fontSize: "0.85rem !important",
    fontWeight: 600,
    lineHeight: 1.2,
    wordBreak: "break-word",
  },
  formSection: {
    marginTop: theme.spacing(2.5),
  },
  formTitle: {
    fontSize: "0.95rem !important",
    fontWeight: 700,
    color: TEXT_TITLE,
    marginBottom: theme.spacing(1.5),
  },
  fieldLabel: {
    color: TEXT_TITLE,
    fontSize: "0.85rem !important",
    fontWeight: 500,
    margin: 0,
    marginBottom: 4,
    lineHeight: 1.1,
  },
  required: {
    color: "#dc2626",
    fontWeight: 600,
    marginLeft: 2,
  },
  field: {
    margin: "0 !important",
    "&.MuiFormControl-root": {
      marginTop: "0 !important",
    },
    "& .MuiFilledInput-root": {
      borderRadius: 10,
      backgroundColor: FIELD_BG,
      border: "1px solid transparent",
      padding: "0 !important",
      transition:
        "background-color 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
      "&:hover": {
        backgroundColor: "#e9eef5",
      },
      "&.Mui-focused": {
        backgroundColor: "white",
        borderColor: ACCENT,
        boxShadow: `0 0 0 3px rgba(29, 78, 216, 0.15)`,
      },
      "&::before, &::after": {
        display: "none",
        content: "none",
        border: "none",
      },
    },
    "& .MuiFilledInput-input, & .MuiInputBase-input, & .MuiInputBase-inputHiddenLabel, & .MuiSelect-select, & .MuiAutocomplete-input": {
      padding: "14px 12px !important",
      fontSize: "0.85rem !important",
      fontWeight: 400,
      color: TEXT_TITLE,
    },
  },
  helperError: {
    color: "#dc2626",
    fontSize: "0.72rem !important",
    marginTop: 4,
  },
  dateField: {
    "& input[type='date']": {
      cursor: "pointer",
    },
    "& input[type='date']::-webkit-calendar-picker-indicator": {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      width: "100%",
      height: "100%",
      padding: 0,
      margin: 0,
      cursor: "pointer",
      opacity: 0,
    },
    "& .MuiFilledInput-root": {
      position: "relative",
    },
  },
  warningBanner: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1, 1.25),
    borderRadius: 8,
    border: "1px solid #fde68a",
    backgroundColor: "#fffbeb",
    color: "#92400e",
    fontSize: "0.78rem !important",
  },

  contentStrip: {
    marginTop: theme.spacing(2.2),
    padding: theme.spacing(1.5, 2),
    borderRadius: 10,
    border: `1px dashed ${BORDER}`,
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "space-evenly",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  contentStripEmpty: {
    color: TEXT_MUTED,
    fontSize: "0.8rem !important",
    fontStyle: "italic",
  },
  contentStripLoading: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: TEXT_MUTED,
    fontSize: "0.8rem !important",
  },
  pageLoading: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1, 1.25),
    borderRadius: 8,
    border: `1px solid ${BORDER}`,
    backgroundColor: "#f8fafc",
    color: TEXT_MUTED,
    fontSize: "0.82rem !important",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  contentStripItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    minWidth: 0,
  },
  contentStripLabel: {
    fontSize: "0.65rem !important",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: TEXT_LABEL,
    lineHeight: 1,
  },
  contentStripValue: {
    fontSize: "0.85rem !important",
    fontWeight: 600,
    color: TEXT_TITLE,
    lineHeight: 1.25,
    marginTop: 4,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  actionsRow: {
    marginTop: theme.spacing(2.5),
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  primaryBtn: {
    color: "white",
    backgroundColor: ACCENT,
    textTransform: "none",
    fontSize: "0.85rem !important",
    fontWeight: 600,
    borderRadius: 8,
    border: `1px solid ${ACCENT}`,
    padding: theme.spacing(0.5, 2.25),
    minWidth: 84,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: ACCENT_DARK,
      borderColor: ACCENT_DARK,
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "#e2e8f0",
      color: "#94a3b8",
      border: "1px solid #cbd5e1",
    },
  },
}));

const SummaryField = ({ label, value, classes }) => (
  <Box className={classes.summaryBodyItem}>
    <Box className={classes.summaryFieldLabel}>{label}</Box>
    <Typography className={classes.summaryFieldValue}>
      {value !== undefined && value !== null && value !== ""
        ? String(value)
        : "—"}
    </Typography>
  </Box>
);

const resolveLocationField = (plant, key) => {
  const value = plant?.[key];
  if (!value) {
    const candidates = [
      plant?.[`${key}Name`],
      plant?.[`${key}_name`],
      plant?.location?.[key],
      plant?.location?.[`${key}Name`],
      plant?.address?.[key],
      plant?.address?.[`${key}Name`],
    ];
    return candidates.find((v) => v) || "";
  }
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return (
      value.name ||
      value[`${key}Name`] ||
      value.title ||
      value.label ||
      value.code ||
      ""
    );
  }
  return String(value);
};

const VehicleSummary = ({ vehicle, classes }) => {
  if (!vehicle) {
    return (
      <Box className={classes.summaryCard}>
        <Box className={`${classes.summaryHeader} ${classes.summaryHeaderEmpty}`}>
          <Box className={`${classes.iconBubble} ${classes.iconBubbleEmpty}`}>
            <DirectionsCarOutlinedIcon className={classes.icon} />
          </Box>
          <Box className={classes.summaryTitleBox}>
            <Box className={classes.summaryTitle}>No vehicle selected</Box>
          </Box>
        </Box>
        <Box className={classes.summaryBodyEmpty}>
          Select a vehicle on the Vehicle Details tab.
        </Box>
      </Box>
    );
  }
  return (
    <Box className={classes.summaryCard}>
      <Box className={classes.summaryHeader}>
        <Box className={classes.iconBubble}>
          <DirectionsCarOutlinedIcon className={classes.icon} />
        </Box>
        <Box className={classes.summaryTitleBox}>
          <Box className={classes.summaryTitle}>
            {vehicle.modelName || "Unnamed Vehicle"}
          </Box>
        </Box>
      </Box>
      <Box className={classes.summaryBody}>
        <SummaryField label="Model Code" value={vehicle.modelCode} classes={classes} />
        <SummaryField label="Year" value={vehicle.yearOfManufacturing} classes={classes} />
        <SummaryField label="Variant" value={vehicle.variant} classes={classes} />
        <SummaryField label="Market" value={vehicle.market} classes={classes} />
      </Box>
    </Box>
  );
};

const PlantSummary = ({ plant, classes }) => {
  if (!plant) {
    return (
      <Box className={classes.summaryCard}>
        <Box className={`${classes.summaryHeader} ${classes.summaryHeaderEmpty}`}>
          <Box className={`${classes.iconBubble} ${classes.iconBubbleEmpty}`}>
            <BusinessOutlinedIcon className={classes.icon} />
          </Box>
          <Box className={classes.summaryTitleBox}>
            <Box className={classes.summaryTitle}>No plant selected</Box>
          </Box>
        </Box>
        <Box className={classes.summaryBodyEmpty}>
          Select a plant on the Plant Details tab.
        </Box>
      </Box>
    );
  }
  const country = resolveLocationField(plant, "country");
  const state = resolveLocationField(plant, "state");
  const city = resolveLocationField(plant, "city");
  return (
    <Box className={classes.summaryCard}>
      <Box className={classes.summaryHeader}>
        <Box className={classes.iconBubble}>
          <BusinessOutlinedIcon className={classes.icon} />
        </Box>
        <Box className={classes.summaryTitleBox}>
          <Box className={classes.summaryTitle}>
            {plant.plantName || "Unnamed Plant"}
          </Box>
        </Box>
      </Box>
      <Box className={classes.summaryBody}>
        <SummaryField label="Plant Code" value={plant.plantCode} classes={classes} />
        <SummaryField label="Country" value={country} classes={classes} />
        <SummaryField label="State" value={state} classes={classes} />
        <SummaryField label="City" value={city} classes={classes} />
      </Box>
    </Box>
  );
};

const ContentStripItem = ({ label, value, classes }) => (
  <Box className={classes.contentStripItem}>
    <Box className={classes.contentStripLabel}>{label}</Box>
    <Box className={classes.contentStripValue} title={value || undefined}>
      {value || "—"}
    </Box>
  </Box>
);

const ContentSummary = ({ vehicle, autoMeta, loading, classes }) => {
  if (!vehicle) {
    return (
      <Box className={classes.contentStrip}>
        <Box className={classes.contentStripEmpty}>
          Select a vehicle to see its uploaded content.
        </Box>
      </Box>
    );
  }
  if (loading) {
    return (
      <Box className={classes.contentStrip}>
        <Box className={classes.contentStripLoading}>
          <CircularProgress size={16} thickness={4} />
          <Box component="span">Loading content details…</Box>
        </Box>
      </Box>
    );
  }
  return (
    <Box className={classes.contentStrip}>
      <ContentStripItem
        label="Language"
        value={autoMeta.languageName}
        classes={classes}
      />
      <ContentStripItem
        label="Publication Type"
        value={autoMeta.publicationTypeName}
        classes={classes}
      />
      <ContentStripItem
        label="Uploaded File"
        value={autoMeta.uploadedFileName}
        classes={classes}
      />
    </Box>
  );
};

const CreateJobInspection = ({ selectedVehicle, selectedPlant }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [makeSelected] = useContext(Context);

  const [technicians, setTechnicians] = useState([]);
  const [technicianArray, setTechnicianArray] = useState([]);
  const [strtDte, setStrtDte] = useState("");
  const [endDte, setEndDte] = useState("");
  const [minED, setMinED] = useState("");
  const [dateHelper, setDateHelper] = useState("");
  const [endDateHelper, setEndDateHelper] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingTech, setLoadingTech] = useState(false);
  const [autoMeta, setAutoMeta] = useState({
    languageUid: "",
    languageName: "",
    publicationTypeUid: "",
    publicationTypeName: "",
    uploadedFileName: "",
  });
  const [autoMetaWarning, setAutoMetaWarning] = useState("");
  const [loadingMeta, setLoadingMeta] = useState(false);

  const today = useMemo(() => moment().format("YYYY-MM-DD"), []);

  const vehicleUid =
    selectedVehicle?.uid ||
    selectedVehicle?.vehicleModelDetailsUid ||
    "";
  const plantUid = selectedPlant?.uid || "";

  useEffect(() => {
    let cancelled = false;
    const loadTechnicians = async () => {
      if (!makeSelected || !makeSelected.uid) {
        setTechnicians([]);
        return;
      }
      setLoadingTech(true);
      try {
        const roles = await dispatch(OAuthActions.getRole());
        const techRole = Array.isArray(roles)
          ? roles.find((r) => r.name === "Technician")
          : null;
        if (!techRole) {
          if (!cancelled) setTechnicians([]);
          return;
        }
        const tchnData = await dispatch(
          OAuthActions.getUsers(makeSelected.uid, techRole.uid)
        );
        if (cancelled) return;
        const list = tchnData?._embedded?.userLists?.filter((u) => u.isActive === true) || [];
        setTechnicians(list);
      } catch (err) {
        if (!cancelled) setTechnicians([]);
      } finally {
        if (!cancelled) setLoadingTech(false);
      }
    };
    loadTechnicians();
    return () => {
      cancelled = true;
    };
  }, [dispatch, makeSelected]);

  useEffect(() => {
    let cancelled = false;
    const resolveMeta = async () => {
      setAutoMeta({
        languageUid: "",
        languageName: "",
        publicationTypeUid: "",
        publicationTypeName: "",
      });
      setAutoMetaWarning("");
      if (!vehicleUid) return;
      setLoadingMeta(true);
      try {
        const result = await dispatch(OAuthActions.checkContentAvailability(vehicleUid));
        if (cancelled) return;

        if (result && result.isAvailable && result.data) {
          const data = result.data;
          // Map the response to autoMeta structure
          setAutoMeta({
            languageUid: (data.languageUid || "").trim(),
            languageName: (data.language || "").trim(),
            publicationTypeUid: (data.publicationTypeUid || "").trim(),
            publicationTypeName: (data.publicationType || "").trim(),
            uploadedFileName: (data.uploadedFileName || "").trim(),
          });
        } else {
          setAutoMetaWarning(
            "No content has been uploaded for this vehicle yet. Upload content from the Content Upload tab before creating an inspection job."
          );
        }
      } catch (err) {
        if (!cancelled) {
          setAutoMetaWarning(
            "Unable to verify uploaded content for this vehicle. Please try again."
          );
        }
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    };
    resolveMeta();
    return () => {
      cancelled = true;
    };
  }, [dispatch, vehicleUid]);

  const handleStartDate = (e) => {
    const value = e.target.value;
    setStrtDte(value);
    if (!value) {
      setDateHelper("");
      setMinED("");
      setEndDte("");
      return;
    }
    const start = new Date(value).getTime();
    const tdy = new Date(today).getTime();
    if (start < tdy) {
      setDateHelper("Start date can't be in the past");
      setMinED("");
      setEndDte("");
      return;
    }
    setDateHelper("");
    const next = new Date(value);
    next.setDate(next.getDate() + 1);
    setMinED(moment(next).format("YYYY-MM-DD"));
    if (endDte && new Date(endDte).getTime() <= start) {
      setEndDte("");
      setEndDateHelper("");
    }
  };

  const handleEndDate = (e) => {
    const value = e.target.value;
    setEndDte(value);
    if (!value) {
      setEndDateHelper("");
      return;
    }
    const end = new Date(value).getTime();
    const start = new Date(strtDte).getTime();
    if (end <= start) {
      setEndDateHelper("End date must be after start date");
    } else {
      setEndDateHelper("");
    }
  };

  const canSubmit =
    !!selectedVehicle &&
    !!selectedPlant &&
    !!strtDte &&
    !!endDte &&
    !dateHelper &&
    !endDateHelper &&
    technicianArray.length > 0 &&
    !!autoMeta.languageName &&
    !!autoMeta.publicationTypeName &&
    !!autoMeta.uploadedFileName &&
    !submitting;

  const resetForm = () => {
    setStrtDte("");
    setEndDte("");
    setMinED("");
    setTechnicianArray([]);
    setDateHelper("");
    setEndDateHelper("");
  };

  const handleCreate = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        languageUid: autoMeta.languageUid,
        vehicleModelUid: vehicleUid,
        plantUid,
        publicationTypeUid: autoMeta.publicationTypeUid,
        technicianList: technicianArray.map((t) => t.uid),
        expectedStartDate: new Date(strtDte).setHours(0, 0, 0, 0),
        expectedEndDate: new Date(endDte).setHours(0, 0, 0, 0),
      };
      const status = await dispatch(OAuthActions.createInspection(payload));
      if (status === 201) {
        dispatch(
          setSnackbar(true, "success", "Inspection Job Successfully Created")
        );
        resetForm();
        history.push("/dashboard");
      } else {
        dispatch(
          setAlertDialog(
            true,
            null,
            "error",
            "Inspection Job",
            "Inspection job creation unsuccessful.\nDetails: " +
            (status && status.message ? status.message : "Unknown error"),
            "OK",
            ""
          )
        );
      }
    } catch (err) {
      dispatch(
        setSnackbar(
          true,
          "error",
          "Unable to create inspection job. Please try again."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.headerRow}>
        <Typography className={classes.headerTitle}>
          Create Inspection Job
        </Typography>
        <Typography className={classes.headerSubtitle}>
          Review the selected vehicle and plant, set the schedule and assign
          technicians.
        </Typography>
      </Box>

      {(loadingMeta || loadingTech) && (
        <Box className={classes.pageLoading}>
          <CircularProgress size={16} thickness={4} />
          <Box component="span">
            {loadingMeta && loadingTech
              ? "Loading content details and technicians…"
              : loadingMeta
                ? "Loading content details…"
                : "Loading technicians…"}
          </Box>
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <VehicleSummary vehicle={selectedVehicle} classes={classes} />
        </Grid>
        <Grid item xs={12} md={6}>
          <PlantSummary plant={selectedPlant} classes={classes} />
        </Grid>
      </Grid>

      <ContentSummary
        vehicle={selectedVehicle}
        autoMeta={autoMeta}
        loading={loadingMeta}
        classes={classes}
      />

      <Box className={classes.formSection}>
        <Typography className={classes.formTitle}>Schedule & Assignment</Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4} md={3}>
            <Box className={classes.fieldLabel}>
              Start Date <span className={classes.required}>*</span>
            </Box>
            <TextField
              fullWidth
              type="date"
              variant="filled"
              hiddenLabel
              value={strtDte}
              onChange={handleStartDate}
              error={!!dateHelper}
              inputProps={{ min: today }}
              InputLabelProps={{ shrink: true }}
              className={`${classes.field} ${classes.dateField}`}
              InputProps={{ disableUnderline: true }}
            />
            {dateHelper && (
              <Box className={classes.helperError}>{dateHelper}</Box>
            )}
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Box className={classes.fieldLabel}>
              End Date <span className={classes.required}>*</span>
            </Box>
            <TextField
              fullWidth
              type="date"
              variant="filled"
              hiddenLabel
              value={endDte}
              onChange={handleEndDate}
              error={!!endDateHelper}
              inputProps={{ min: minED }}
              InputLabelProps={{ shrink: true }}
              className={`${classes.field} ${classes.dateField}`}
              InputProps={{ disableUnderline: true }}
              disabled={!strtDte || !!dateHelper}
            />
            {endDateHelper && (
              <Box className={classes.helperError}>{endDateHelper}</Box>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={1} style={{ marginTop: 12 }}>
          <Grid item xs={12} sm={8} md={6}>
            <Box className={classes.fieldLabel}>
              Technicians <span className={classes.required}>*</span>
            </Box>
            <Autocomplete
              multiple
              options={technicians}
              value={technicianArray}
              onChange={(event, newValue) => setTechnicianArray(newValue)}
              loading={loadingTech}
              getOptionLabel={(option) =>
                `${option.firstName || ""} ${option.lastName || ""}`.trim() ||
                option.email ||
                ""
              }
              getOptionSelected={(option, value) => option.uid === value.uid}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    size="small"
                    key={option.uid}
                    label={`${option.firstName || ""} ${option.lastName || ""}`.trim()}
                    {...getTagProps({ index })}
                  />
                ))
              }
              PaperComponent={({ children }) => (
                <Paper
                  style={{
                    borderRadius: 10,
                    fontSize: "1rem",
                  }}
                >
                  {children}
                </Paper>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="filled"
                  hiddenLabel
                  placeholder={
                    technicianArray.length === 0
                      ? "Select one or more technicians"
                      : ""
                  }
                  className={classes.field}
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                  }}
                />
              )}
            />
          </Grid>
        </Grid>

        {autoMetaWarning && (
          <Box className={classes.warningBanner}>{autoMetaWarning}</Box>
        )}

        <Box className={classes.actionsRow}>
          <Button
            className={classes.primaryBtn}
            variant="contained"
            disableElevation
            onClick={handleCreate}
            disabled={!canSubmit}
            startIcon={
              submitting ? (
                <CircularProgress size={16} style={{ color: "white" }} />
              ) : null
            }
          >
            {submitting ? "Creating..." : "Create"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateJobInspection;
